import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Share2, Lock, Mail, Download, Info, RotateCcw, ChevronLeft, ChevronRight, X, Copy, Check } from 'lucide-react';
import SmartCalculatorsSidebar from '../../components/common/SmartCalculatorsSidebar';
import Navbar from '../../components/common/Navbar';

const TaxCalculator = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tipIndex, setTipIndex] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareText = "Managing finances doesn't have to be messy. Kapitalised helps you stay organised and updated in one place. Take a look here: kapitalised.com";
  const shareUrl = "https://kapitalised.com";

  const handleShare = (platform) => {
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);
    
    let shareLink = '';
    
    switch (platform) {
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodedText}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?text=${encodedText}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      default:
        break;
    }
    
    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };
  
  // Step 1 fields
  const [fy, setFy] = useState('2025-26');
  const [age, setAge] = useState('below60');
  
  // Step 2 fields - Income Details
  const [grossSalary, setGrossSalary] = useState('');
  const [exemptAllowance, setExemptAllowance] = useState('');
  const [incomeFromInterest, setIncomeFromInterest] = useState('');
  const [incomeFromOtherSources, setIncomeFromOtherSources] = useState('');
  const [rentalIncome, setRentalIncome] = useState('');
  const [homeLoanInterestSelfOccupied, setHomeLoanInterestSelfOccupied] = useState('');
  const [homeLoanInterestLetOut, setHomeLoanInterestLetOut] = useState('');
  
  // Step 3 fields - Deductions
  const [deduction80C, setDeduction80C] = useState('');
  const [deduction80TTA, setDeduction80TTA] = useState('');
  const [deduction80D, setDeduction80D] = useState('');
  const [deduction80G, setDeduction80G] = useState('');
  const [deduction80CCD2, setDeduction80CCD2] = useState('');
  const [deduction80CCD, setDeduction80CCD] = useState('');
  const [deduction80EEA, setDeduction80EEA] = useState('');
  const [otherDeductions, setOtherDeductions] = useState('');

  const taxSlabs = {
    old: {
      below60: [
        { limit: 250000, rate: 0 },
        { limit: 500000, rate: 5 },
        { limit: 1000000, rate: 20 },
        { limit: Infinity, rate: 30 }
      ],
      '60to80': [
        { limit: 300000, rate: 0 },
        { limit: 500000, rate: 5 },
        { limit: 1000000, rate: 20 },
        { limit: Infinity, rate: 30 }
      ],
      above80: [
        { limit: 500000, rate: 0 },
        { limit: 1000000, rate: 20 },
        { limit: Infinity, rate: 30 }
      ]
    },
    'new-2024-25': [
      { limit: 300000, rate: 0 },
      { limit: 600000, rate: 5 },
      { limit: 900000, rate: 10 },
      { limit: 1200000, rate: 15 },
      { limit: 1500000, rate: 20 },
      { limit: Infinity, rate: 30 }
    ],
    'new-2025-26': [
      { limit: 400000, rate: 0 },
      { limit: 800000, rate: 5 },
      { limit: 1200000, rate: 10 },
      { limit: 1600000, rate: 15 },
      { limit: 2000000, rate: 20 },
      { limit: 2400000, rate: 25 },
      { limit: Infinity, rate: 30 }
    ]
  };

  const calculateTax = (income, slabs) => {
    let tax = 0;
    let previousLimit = 0;
    for (let slab of slabs) {
      if (income > previousLimit) {
        const taxableInSlab = Math.min(income - previousLimit, slab.limit - previousLimit);
        tax += (taxableInSlab * slab.rate) / 100;
      }
      previousLimit = slab.limit;
      if (income <= slab.limit) break;
    }
    return tax;
  };

  const calculations = useMemo(() => {
    const gross = parseFloat(grossSalary) || 0;
    const exempt = parseFloat(exemptAllowance) || 0;
    const interestIncome = parseFloat(incomeFromInterest) || 0;
    const otherSourcesIncome = parseFloat(incomeFromOtherSources) || 0;
    const rental = parseFloat(rentalIncome) || 0;
    const homeLoanSelf = parseFloat(homeLoanInterestSelfOccupied) || 0;
    const homeLoanLet = parseFloat(homeLoanInterestLetOut) || 0;
    
    const ded80C = Math.min(parseFloat(deduction80C) || 0, 150000);
    const ded80TTA = Math.min(parseFloat(deduction80TTA) || 0, 10000);
    const ded80D = Math.min(parseFloat(deduction80D) || 0, 65000); // Max 65k default (25k self + 25k parents + 15k checkup)
    const ded80G = parseFloat(deduction80G) || 0;
    const ded80CCD2 = parseFloat(deduction80CCD2) || 0; // No cap - depends on employer contribution
    const ded80CCD = Math.min(parseFloat(deduction80CCD) || 0, 50000); // 80CCD(1B) capped at 50k
    const ded80EEA = Math.min(parseFloat(deduction80EEA) || 0, 150000);
    const otherDed = parseFloat(otherDeductions) || 0;

    const salaryIncome = gross - exempt;
    const housePropertyIncome = rental - homeLoanLet - Math.min(homeLoanSelf, 200000);
    const grossIncome = salaryIncome + interestIncome + otherSourcesIncome + Math.max(housePropertyIncome, -200000);

    const standardDeductionOld = 50000;
    const totalDeductionsOld = ded80C + ded80TTA + ded80D + ded80G + ded80CCD2 + ded80CCD + ded80EEA + otherDed;
    const taxableIncomeOld = Math.max(0, grossIncome - standardDeductionOld - totalDeductionsOld);
    
    const ageKey = age === '60to80' ? '60to80' : age;
    const taxOld = Math.round(calculateTax(taxableIncomeOld, taxSlabs.old[ageKey]));
    const rebateOld = taxableIncomeOld <= 500000 ? Math.min(taxOld, 12500) : 0;
    const taxAfterRebateOld = taxOld - rebateOld;
    const cessOld = Math.round(taxAfterRebateOld * 0.04);
    const totalTaxOld = taxAfterRebateOld + cessOld;

    const standardDeductionNew = fy === '2025-26' ? 75000 : 50000;
    const taxableIncomeNew = Math.max(0, grossIncome - standardDeductionNew);
    const newSlabs = fy === '2025-26' ? taxSlabs['new-2025-26'] : taxSlabs['new-2024-25'];
    const taxNew = Math.round(calculateTax(taxableIncomeNew, newSlabs));
    const rebateLimit = fy === '2025-26' ? 1200000 : 700000;
    const rebateAmount = fy === '2025-26' ? 60000 : 25000;
    let rebate = taxableIncomeNew <= rebateLimit ? Math.min(taxNew, rebateAmount) : 0;
    
    let marginalReliefApplied = false;
    const marginalLimit = fy === '2025-26' ? 1275000 : 775000;
    if (taxableIncomeNew > rebateLimit && taxableIncomeNew <= marginalLimit) {
      const excessIncome = taxableIncomeNew - rebateLimit;
      if (taxNew > excessIncome) {
        marginalReliefApplied = true;
        rebate = taxNew - excessIncome;
      }
    }
    
    const taxAfterRebate = taxNew - rebate;
    const cessNew = Math.round(taxAfterRebate * 0.04);
    const totalTaxNew = taxAfterRebate + cessNew;

    const monthlyTakeHome = (grossIncome - Math.min(totalTaxOld, totalTaxNew)) / 12;
    const potentialSavings = ded80C < 150000 ? 150000 - ded80C : 0;

    return {
      grossIncome,
      old: { totalTax: totalTaxOld, taxableIncome: taxableIncomeOld },
      new: { totalTax: totalTaxNew, taxableIncome: taxableIncomeNew, marginalReliefApplied },
      better: totalTaxOld < totalTaxNew ? 'old' : 'new',
      savings: Math.abs(totalTaxOld - totalTaxNew),
      monthlyTakeHome,
      potentialSavings
    };
  }, [fy, age, grossSalary, exemptAllowance, incomeFromInterest, incomeFromOtherSources, rentalIncome, homeLoanInterestSelfOccupied, homeLoanInterestLetOut, deduction80C, deduction80TTA, deduction80D, deduction80G, deduction80CCD2, deduction80CCD, deduction80EEA, otherDeductions]);

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(num);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsPremium(true);
    }
  };

  // Styles
  const badgeStyles = {
    background: 'linear-gradient(180deg, #FFB643 0%, #FFB93F 100%)',
    border: '0.62px solid #CF902C',
    boxShadow: 'inset 0px 1px 3px rgba(255, 255, 255, 0.58), inset 0px -1px 2px rgba(207, 144, 44, 0.5), 0px 2px 5px rgba(208, 144, 44, 0.14)',
    color: '#603800',
    fontFamily: 'Onest, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    letterSpacing: '-0.02em',
    padding: '6px 16px',
    borderRadius: '6px',
  };

  const sectionHeadingStyles = {
    fontFamily: 'Onest, sans-serif',
    fontWeight: 600,
    fontSize: '20px',
    letterSpacing: '-0.03em',
    color: '#004036',
  };

  const inputStyles = {
    fontFamily: 'Onest, sans-serif',
    fontWeight: 500,
    fontSize: '18px',
    letterSpacing: '-0.02em',
    color: '#000000',
  };

  const selectStyles = {
    ...inputStyles,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundPosition: 'right 16px center',
    backgroundSize: '16px',
    backgroundRepeat: 'no-repeat',
  };

  const labelStyles = {
    fontFamily: 'Onest, sans-serif',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  };

  const rupeeStyles = {
    fontFamily: 'Onest, sans-serif',
    fontSize: '18px',
    fontWeight: 500,
    color: '#9CA3AF',
  };

  // Render tooltip inline
  const renderTooltip = (id, text) => (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        onMouseEnter={() => setActiveTooltip(id)}
        onMouseLeave={() => setActiveTooltip(null)}
        onClick={(e) => {
          e.preventDefault();
          setActiveTooltip(activeTooltip === id ? null : id);
        }}
        className="text-gray-400 hover:text-gray-600 focus:outline-none ml-1"
      >
        <Info className="w-4 h-4" />
      </button>
      {activeTooltip === id && (
        <div className="absolute z-10 w-64 p-3 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg right-0 top-6">
          <div className="absolute -top-2 right-2 w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45"></div>
          {text}
        </div>
      )}
    </div>
  );

  // Render step indicator
  const renderStepIndicator = (number, isActive, isCompleted) => {
    if (isActive || isCompleted) {
      return (
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
          style={{ 
            backgroundColor: '#004036',
            fontFamily: 'Onest, sans-serif',
            fontWeight: 600,
            color: '#4ade80'
          }}
        >
          {number}
        </div>
      );
    }
    
    return (
      <div 
        className="w-10 h-10 rounded-full flex items-center justify-center text-lg border-2"
        style={{ 
          backgroundColor: '#F5F5F5',
          borderColor: '#E0E0E0',
          fontFamily: 'Onest, sans-serif',
          fontWeight: 600,
          color: '#9CA3AF'
        }}
      >
        {number}
      </div>
    );
  };

  // CSS to hide number input spinners
  const hideSpinnerCSS = `
    input[type="number"]::-webkit-outer-spin-button,
    input[type="number"]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type="number"] {
      -moz-appearance: textfield;
    }
  `;


  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Onest:wght@100;200;300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <style>{hideSpinnerCSS}</style>
      <style>{`
        @media (min-width: 640px) and (max-width: 1023px) {
          .hero-section { padding: 32px 40px 80px 40px !important; }
          .seo-section { padding: 48px 40px 64px 40px !important; }
        }
        @media (max-width: 639px) {
          .hero-section { padding: 32px 20px 60px 20px !important; }
          .seo-section { padding: 32px 20px 48px 20px !important; }
          .tax-summary-header { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
          .summary-cards { flex-direction: column !important; }
        }
      `}</style>
      
      <Navbar background="#1a4d3e" />

      {/* Hero Section with Gradient */}
      <div className="hero-section" style={{ background: 'linear-gradient(180deg, #1a4d3e 0%, #2d6b5a 30%, #4a9d7f 60%, #7ec9a8 85%, #ffffff 100%)', padding: '32px 100px 80px 100px' }}>
        
        {/* Breadcrumb */}
        <div style={{ marginBottom: '18px' }}>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontWeight: 400 }}>Home</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.55)', textDecoration: 'none', fontWeight: 400 }}>Calculators</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>Income Tax Calculator</span>
        </div>

        {/* Title + Share */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 className="landing-title" style={{ fontFamily: 'Instrument Serif, serif', fontWeight: 400, fontSize: '62px', color: '#ffffff', margin: '0 0 10px 0', lineHeight: 1 }}>
              Income Tax Calculator
            </h1>
            <span style={badgeStyles}>Latest Budget 2026</span>
          </div>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowShareMenu(!showShareMenu)} style={{ fontFamily: 'Onest, sans-serif', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#ffffff', fontWeight: 600, fontSize: '14px', paddingBottom: '8px' }}>
              <Share2 size={16} /> SHARE
            </button>
            {showShareMenu && (
              <div style={{ position: 'absolute', right: 0, top: '36px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', padding: '12px', zIndex: 50, minWidth: '220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontFamily: 'Onest', fontWeight: 600, fontSize: '13px', color: '#111827' }}>Share via</span>
                  <button onClick={() => setShowShareMenu(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={16} /></button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  {[
                    { id: 'whatsapp', label: 'WhatsApp', bg: '#25D366', icon: <svg width="20" height="20" fill="white" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
                    { id: 'facebook', label: 'Facebook', bg: '#1877F2', icon: <svg width="20" height="20" fill="white" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
                    { id: 'twitter',  label: 'X',        bg: '#000',     icon: <svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                    { id: 'copy',     label: copied ? 'Copied!' : 'Copy', bg: '#e5e7eb', icon: copied ? <Check size={18} color="#16a34a" /> : <Copy size={18} color="#374151" /> },
                  ].map(({ id, label, bg, icon }) => (
                    <button key={id} onClick={() => handleShare(id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '8px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
                      <span style={{ fontFamily: 'Onest', fontSize: '11px', color: '#6b7280' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Two Column Layout - Cards aligned at top */}
        <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
          
          {/* LEFT - Calculator Card */}
          <div style={{ flex: 1 }}>
            <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-2xl">
              {step !== 4 && (
                <div className="flex items-center gap-3 mb-4">
                  {renderStepIndicator(1, step === 1, step > 1)}
                  {renderStepIndicator(2, step === 2, step > 2)}
                  {renderStepIndicator(3, step === 3, step > 3)}
                </div>
              )}

              {/* Step 1 */}
              {step === 1 && (
                <div>
                  <h2 style={sectionHeadingStyles} className="mb-6">Let's start with basic details</h2>
                  <div className="space-y-5 mb-10">
                    <div>
                      <label style={labelStyles} className="block mb-2">Financial Year</label>
                      <select value={fy} onChange={(e) => setFy(e.target.value)} style={selectStyles} className="w-full px-4 py-3.5 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800 appearance-none">
                        <option value="2025-26">FY 2025-2026</option>
                        <option value="2024-25">FY 2024-2025</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyles} className="block mb-2">Age Group</label>
                      <select value={age} onChange={(e) => setAge(e.target.value)} style={selectStyles} className="w-full px-4 py-3.5 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800 appearance-none">
                        <option value="below60">Below 60 years</option>
                        <option value="60to80">60 to 80 years (Senior Citizen)</option>
                        <option value="above80">Above 80 years (Super Senior Citizen)</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setStep(2)} style={{ fontFamily: 'Onest, sans-serif', backgroundColor: '#115e59' }} className="w-full md:w-auto px-16 py-3.5 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity">Continue</button>
                  </div>
                </div>
              )}
            {/* Step 2 - Income Details */}
            {step === 2 && (
              <div>
                <h2 style={sectionHeadingStyles} className="mb-6">
                  Add your income details
                </h2>
                
                <div className="space-y-5 mb-8">
                  {/* Row 1 - Gross Salary full width */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label style={labelStyles}>Gross Salary</label>
                      {renderTooltip("grossSalary", "Salary before any deductions like house rent, travel allowance, standard deduction, or professional tax.")}
                    </div>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                      <input 
                        type="text"
                        inputMode="numeric"
                        value={grossSalary}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setGrossSalary(val);
                        }}
                        placeholder="0"
                        style={inputStyles}
                        className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                      />
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Exempt Allowances</label>
                        {renderTooltip("exemptAllowance", "HRA, LTA, Professional Tax and any Other Exemptions")}
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={exemptAllowance}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setExemptAllowance(val);
                          }}
                          placeholder="0"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Income from Interest</label>
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={incomeFromInterest}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setIncomeFromInterest(val);
                          }}
                          placeholder="0"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 3 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Income from Other Sources</label>
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={incomeFromOtherSources}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setIncomeFromOtherSources(val);
                          }}
                          placeholder="0"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Rental Income Received</label>
                        {renderTooltip("rentalIncome", "Total rent received in one year from a rented property")}
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={rentalIncome}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setRentalIncome(val);
                          }}
                          placeholder="0"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 4 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Interest on Home Loan (Self Occupied)</label>
                        {renderTooltip("homeLoanSelf", "Interest paid on a home loan for a house you live in")}
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={homeLoanInterestSelfOccupied}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setHomeLoanInterestSelfOccupied(val);
                          }}
                          placeholder="0"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Interest on Home Loan (Let Out)</label>
                        {renderTooltip("homeLoanLet", "Interest paid on a home loan for a rented property")}
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={homeLoanInterestLetOut}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setHomeLoanInterestLetOut(val);
                          }}
                          placeholder="0"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-row justify-end items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    style={{ fontFamily: 'Onest, sans-serif' }}
                    className="px-8 py-3.5 text-gray-700 font-medium hover:text-gray-900 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    style={{ fontFamily: 'Onest, sans-serif', backgroundColor: '#115e59' }}
                    className="px-16 py-3.5 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 3 - Deductions */}
            {step === 3 && (
              <div>
                <h2 style={sectionHeadingStyles} className="mb-6">
                  Deductions - applicable only for Old Regime
                </h2>
                
                <div className="space-y-5 mb-8">
                  {/* Row 1 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Basic Deduction - 80C</label>
                        {renderTooltip("80c", "Money invested in tax-saving options like PPF, ELSS funds, LIC premium, etc. (up to ₹1.5 lakh)")}
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={deduction80C}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setDeduction80C(val);
                          }}
                          placeholder="0 (Max 1,50,000)"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Interest on Deposits - 80TTA</label>
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={deduction80TTA}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setDeduction80TTA(val);
                          }}
                          placeholder="0 (Max 10,000)"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Medical Insurance - 80D</label>
                        {renderTooltip("80d", "Money paid for health insurance and health checkups for yourself, family, and parents")}
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={deduction80D}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setDeduction80D(val);
                          }}
                          placeholder="0"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Donations to Charity - 80G</label>
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={deduction80G}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setDeduction80G(val);
                          }}
                          placeholder="0"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 3 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Employer's NPS - 80CCD(2)</label>
                        {renderTooltip("80ccd2", "Money contributed by your employer to your NPS account (Section 80CCD(2))")}
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={deduction80CCD2}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setDeduction80CCD2(val);
                          }}
                          placeholder="0"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Self NPS - 80CCD</label>
                        {renderTooltip("80ccd", "Includes self/voluntary contributions made to NPS under Sections 80CCD(1) and 80CCD(1B)")}
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={deduction80CCD}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setDeduction80CCD(val);
                          }}
                          placeholder="0 (Max 50,000)"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 4 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Housing Loan Interest - 80EEA</label>
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={deduction80EEA}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setDeduction80EEA(val);
                          }}
                          placeholder="0 (Max 1,50,000)"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label style={labelStyles}>Any Other Deduction</label>
                        {renderTooltip("otherDed", "Enter any other deduction amount not covered above")}
                      </div>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2" style={rupeeStyles}>₹</span>
                        <input 
                          type="text"
                          inputMode="numeric"
                          value={otherDeductions}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setOtherDeductions(val);
                          }}
                          placeholder="0"
                          style={inputStyles}
                          className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-800"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-row justify-end items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    style={{ fontFamily: 'Onest, sans-serif' }}
                    className="px-8 py-3.5 text-gray-700 font-medium hover:text-gray-900 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(4)}
                    style={{ fontFamily: 'Onest, sans-serif', backgroundColor: '#115e59' }}
                    className="px-16 py-3.5 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
                  >
                    Calculate Tax
                  </button>
                </div>
              </div>
            )}

            {/* Results - Step 4 */}
            {step === 4 && (
              <div>
                {/* Header with Title and Recalculate */}
                <div className="flex items-center justify-between mb-6">
                  <h2 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700 }} className="text-2xl md:text-3xl text-gray-900">
                    Your tax summary
                  </h2>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    style={{ fontFamily: 'Onest, sans-serif' }}
                    className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <RotateCcw className="w-5 h-5" />
                    <span className="text-base font-medium">Recalculate</span>
                  </button>
                </div>

                {/* Recommendation Banner */}
                <div 
                  className="p-4 rounded-xl mb-6 flex items-start gap-2"
                  style={{ backgroundColor: '#115e59' }}
                >
                  <span style={{ color: '#4ade80', lineHeight: '1.75rem' }}>✦</span>
                  <p style={{ fontFamily: 'Onest, sans-serif' }} className="text-white text-base md:text-lg">
                    <span style={{ color: '#4ade80' }} className="font-semibold">{calculations.better === 'new' ? 'New Tax Regime is recommended' : 'Old Tax Regime is recommended'}</span>
                    {' '}for you and can help you save approx <span className="font-semibold">₹{(calculations.savings / 100000).toFixed(1)} lakh</span> in taxes.
                  </p>
                </div>

                {/* Tax Cards with Breakdown Combined */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Old Regime Card */}
                  <div className="p-5 rounded-2xl border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 style={{ fontFamily: 'Onest, sans-serif' }} className="text-base font-bold text-gray-900">Old Regime</h3>
                      {calculations.better === 'old' && (
                        <span style={{ fontFamily: 'Onest, sans-serif', backgroundColor: '#115e59' }} className="text-white text-xs font-semibold px-3 py-1 rounded-full">RECOMMENDED</span>
                      )}
                    </div>
                    <div style={{ fontFamily: 'Onest, sans-serif' }} className="text-3xl font-bold text-gray-900 mb-4">
                      ₹ {calculations.old.totalTax.toLocaleString('en-IN')}
                    </div>
                    
                    {/* Breakdown */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="text-gray-500">Gross Income</span>
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="text-gray-900">₹ {calculations.grossIncome.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="text-gray-500">Standard Deduction</span>
                        <span style={{ fontFamily: 'Onest, sans-serif', color: '#16a34a' }}>-₹ 50,000</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="text-gray-500">Taxable Income</span>
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="text-gray-900">₹ {calculations.old.taxableIncome.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-3 mt-3 flex justify-between">
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="font-semibold text-gray-900">Tax Payable</span>
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="font-semibold text-gray-900">₹ {calculations.old.totalTax.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>

                  {/* New Regime Card */}
                  <div className="p-5 rounded-2xl border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 style={{ fontFamily: 'Onest, sans-serif' }} className="text-base font-bold text-gray-900">New Regime</h3>
                      {calculations.better === 'new' && (
                        <span style={{ fontFamily: 'Onest, sans-serif', backgroundColor: '#115e59' }} className="text-white text-xs font-semibold px-3 py-1 rounded-full">RECOMMENDED</span>
                      )}
                    </div>
                    <div style={{ fontFamily: 'Onest, sans-serif' }} className="text-3xl font-bold text-gray-900 mb-4">
                      ₹ {calculations.new.totalTax.toLocaleString('en-IN')}
                    </div>
                    
                    {/* Breakdown */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="text-gray-500">Gross Income</span>
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="text-gray-900">₹ {calculations.grossIncome.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="text-gray-500">Standard Deduction</span>
                        <span style={{ fontFamily: 'Onest, sans-serif', color: '#16a34a' }}>-₹ {fy === '2025-26' ? '75,000' : '50,000'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="text-gray-500">Taxable Income</span>
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="text-gray-900">₹ {calculations.new.taxableIncome.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-3 mt-3 flex justify-between">
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="font-semibold text-gray-900">Tax Payable</span>
                        <span style={{ fontFamily: 'Onest, sans-serif' }} className="font-semibold text-gray-900">₹ {calculations.new.totalTax.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Cards - Always Side by Side */}
                <div className="flex gap-4 mb-6">
                  <div className="flex-1 p-4 rounded-2xl" style={{ backgroundColor: '#E0E7FF' }}>
                    <p style={{ fontFamily: 'Onest, sans-serif' }} className="text-sm font-semibold text-gray-700 mb-1">Gross Income</p>
                    <p style={{ fontFamily: 'Onest, sans-serif' }} className="text-xl md:text-2xl font-bold text-gray-900">₹ {calculations.grossIncome.toLocaleString('en-IN')}</p>
                  </div>
                  <div className="flex-1 p-4 rounded-2xl" style={{ backgroundColor: '#F5D0FE' }}>
                    <p style={{ fontFamily: 'Onest, sans-serif' }} className="text-sm font-semibold text-gray-700 mb-1">Monthly Take-home</p>
                    <p style={{ fontFamily: 'Onest, sans-serif' }} className="text-xl md:text-2xl font-bold text-gray-900">₹ {Math.round(calculations.monthlyTakeHome).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="flex-1 p-4 rounded-2xl" style={{ backgroundColor: '#FDE68A' }}>
                    <p style={{ fontFamily: 'Onest, sans-serif' }} className="text-sm font-semibold text-gray-700 mb-1">Tax Saved</p>
                    <p style={{ fontFamily: 'Onest, sans-serif' }} className="text-xl md:text-2xl font-bold text-gray-900">₹ {Math.round(calculations.savings).toLocaleString('en-IN')}</p>
                  </div>
                </div>

                {/* Smart Money Tips - Carousel with 1 tip at a time, 3 total */}
                <div style={{ backgroundColor: '#115e59' }} className="rounded-2xl p-5 md:p-6">
                  {/* Header with navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 style={{ fontFamily: 'Onest, sans-serif' }} className="text-lg font-bold text-white">Smart Money Tips</h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setTipIndex(prev => Math.max(0, prev - 1))}
                        className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-50"
                        disabled={tipIndex === 0}
                      >
                        <ChevronLeft className="w-4 h-4 text-gray-700" />
                      </button>
                      <div className="flex items-center gap-1.5">
                        {[0, 1, 2].map((idx) => (
                          <div
                            key={idx}
                            className={`w-2 h-2 rounded-full transition-colors ${tipIndex === idx ? 'bg-white' : 'bg-white/40'}`}
                          />
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setTipIndex(prev => Math.min(2, prev + 1))}
                        className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-50"
                        disabled={tipIndex === 2}
                      >
                        <ChevronRight className="w-4 h-4 text-gray-700" />
                      </button>
                    </div>
                  </div>

                  {/* Tips Content - Show 1 tip at a time */}
                  <div className="p-4 rounded-xl" style={{ backgroundColor: '#0d4f47' }}>
                    {tipIndex === 0 && (
                      <>
                        <p style={{ fontFamily: 'Onest, sans-serif' }} className="font-semibold text-white mb-2">🚀 Start a SIP with your tax savings</p>
                        <p style={{ fontFamily: 'Onest, sans-serif' }} className="text-sm text-white/80">
                          You're saving ₹{Math.round(calculations.savings).toLocaleString('en-IN')} this year! Start a monthly SIP of just ₹{Math.round(calculations.savings / 12).toLocaleString('en-IN')} in an index fund. 
                          At 12% returns, this grows to <span className="text-green-300 font-semibold">₹{Math.round((calculations.savings / 12) * 12 * 1.12).toLocaleString('en-IN')}</span> in 1 year — no lock-in!
                        </p>
                      </>
                    )}
                    {tipIndex === 1 && (
                      <>
                        <p style={{ fontFamily: 'Onest, sans-serif' }} className="font-semibold text-white mb-2">Skip FDs, Choose Mutual Funds</p>
                        <p style={{ fontFamily: 'Onest, sans-serif' }} className="text-sm text-white/80">
                          Instead of putting ₹{Math.round(calculations.savings).toLocaleString('en-IN')} in an FD at 7% (earning ₹{Math.round(calculations.savings * 0.07).toLocaleString('en-IN')}), invest in equity mutual funds at 12% and earn <span className="text-green-300 font-semibold">₹{Math.round(calculations.savings * 0.12).toLocaleString('en-IN')}</span> — that's ₹{Math.round(calculations.savings * 0.05).toLocaleString('en-IN')} extra per year!
                        </p>
                      </>
                    )}
                    {tipIndex === 2 && (
                      <>
                        <p style={{ fontFamily: 'Onest, sans-serif' }} className="font-semibold text-white mb-2">5-Year Wealth Goal</p>
                        <p style={{ fontFamily: 'Onest, sans-serif' }} className="text-sm text-white/80">
                          Invest ₹{Math.round(calculations.savings / 12).toLocaleString('en-IN')} monthly for 5 years at 12% returns. Build a corpus of <span className="text-green-300 font-semibold">₹{Math.round((calculations.savings / 12) * 60 * 1.35).toLocaleString('en-IN')}</span>! Start early, stay consistent. No lock-in with index funds.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            </div>
            {/* Calculator Updated Text - Below Card */}
            <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', marginTop: '16px' }}>
              <span style={{ color: '#115e59' }}>calculator updated on </span>
              <span style={{ fontWeight: 600, color: '#1f2937' }}>10th Feb 2026</span>
            </p>
          </div>
          {/* End Calculator Card Column */}

          {/* RIGHT - Sidebar */}
          <SmartCalculatorsSidebar currentSlug="income-tax" rightOffset="100px" />

        </div>
      </div>
      {/* End Hero Section with Gradient */}

      {/* SEO Section - White Background, Outside Gradient */}
      <div className="seo-section" style={{ backgroundColor: '#ffffff', padding: '48px 100px', paddingRight: '450px' }}>
        
        {/* Main Hero */}
        <div style={{ marginBottom: '48px' }}>
          <h2 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '36px', color: '#1f2937', marginBottom: '16px' }}>
            Kapitalised Income Tax Calculator FY 2026-27
          </h2>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '20px', color: '#115e59', fontWeight: 600, marginBottom: '16px' }}>
            Calculate Your Income Tax Online in Seconds
          </p>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '24px' }}>
            Use the Kapitalised Income Tax Calculator to instantly calculate your tax liability for FY 2026-27. Compare the old vs new tax regime, check updated income tax slab rates, and plan your taxes smarter.
          </p>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ fontFamily: 'Onest, sans-serif', backgroundColor: '#115e59', color: '#ffffff', padding: '14px 32px', borderRadius: '8px', fontWeight: 600, fontSize: '15px', border: 'none', cursor: 'pointer' }}>
            Calculate Your Tax Now
          </button>
        </div>

        {/* What is */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>What is the Kapitalised Income Tax Calculator?</h3>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            The Kapitalised Income Tax Calculator is a free online tool that helps you:
          </p>
          <ul style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#374151', lineHeight: 1.8, paddingLeft: '0', listStyle: 'none' }}>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}><span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#115e59" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M9 9h6v6H9z"/></svg></span>Calculate income tax for FY 2026-27</li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}><span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#115e59" strokeWidth="2"><path d="M3 3v18h18"/><path d="M7 16l4-8 4 5 5-6"/></svg></span>Compare old and new tax regimes</li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}><span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#115e59" strokeWidth="2"><path d="M9 12l2 2 4-4"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg></span>Apply deductions under 80C, 80D, 80G</li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}><span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#115e59" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg></span>Check rebate under Section 87A</li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><span style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '14px', color: '#115e59' }}>₹</span></span>Estimate surcharge and 4% cess</li>
          </ul>
        </div>

        {/* New Regime Tax Slabs */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>Income Tax Slab Rates FY 2026-27 (New Regime)</h3>
          <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#115e59' }}>
                  <th style={{ fontFamily: 'Onest, sans-serif', textAlign: 'left', color: '#fff', fontWeight: 600, padding: '14px 16px' }}>Income Slab</th>
                  <th style={{ fontFamily: 'Onest, sans-serif', textAlign: 'right', color: '#fff', fontWeight: 600, padding: '14px 16px' }}>Tax Rate</th>
                </tr>
              </thead>
              <tbody>
                {[['Up to ₹4 lakh', 'Nil'], ['₹4–8 lakh', '5%'], ['₹8–12 lakh', '10%'], ['₹12–16 lakh', '15%'], ['₹16–20 lakh', '20%'], ['₹20–24 lakh', '25%'], ['Above ₹24 lakh', '30%']].map(([slab, rate], i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 === 1 ? '#f9fafb' : '#fff', borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ fontFamily: 'Onest, sans-serif', padding: '12px 16px', color: '#374151', fontSize: '15px' }}>{slab}</td>
                    <td style={{ fontFamily: 'Onest, sans-serif', padding: '12px 16px', color: '#1f2937', fontSize: '15px', fontWeight: 600, textAlign: 'right' }}>{rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#6b7280', marginTop: '12px' }}>Income up to ₹12 lakh may have zero tax due to rebate (conditions apply).</p>
        </div>

        {/* Old Regime */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>Income Tax Slab Rates FY 2026-27 (Old Regime)</h3>
          <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#115e59' }}>
                  <th style={{ fontFamily: 'Onest, sans-serif', textAlign: 'left', color: '#fff', fontWeight: 600, padding: '14px 16px' }}>Income Slab</th>
                  <th style={{ fontFamily: 'Onest, sans-serif', textAlign: 'right', color: '#fff', fontWeight: 600, padding: '14px 16px' }}>Tax Rate</th>
                </tr>
              </thead>
              <tbody>
                {[['Up to ₹2.5 lakh', 'Nil'], ['₹2.5–5 lakh', '5%'], ['₹5–10 lakh', '20%'], ['Above ₹10 lakh', '30%']].map(([slab, rate], i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 === 1 ? '#f9fafb' : '#fff', borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ fontFamily: 'Onest, sans-serif', padding: '12px 16px', color: '#374151', fontSize: '15px' }}>{slab}</td>
                    <td style={{ fontFamily: 'Onest, sans-serif', padding: '12px 16px', color: '#1f2937', fontSize: '15px', fontWeight: 600, textAlign: 'right' }}>{rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Standard Deduction */}
        <div style={{ marginBottom: '48px', backgroundColor: '#115e59', padding: '24px', borderRadius: '16px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#ffffff', marginBottom: '16px' }}>Standard Deduction FY 2026-27</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', padding: '20px', borderRadius: '12px' }}>
              <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '28px', fontWeight: 700, color: '#ffffff' }}>₹50,000</span>
              <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>Old Regime</p>
            </div>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', padding: '20px', borderRadius: '12px' }}>
              <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '28px', fontWeight: 700, color: '#ffffff' }}>₹75,000</span>
              <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>New Regime</p>
            </div>
          </div>
        </div>

        {/* How to Use */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '20px' }}>How to Use</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { s: '1', t: 'Select FY 2026-27' },
              { s: '2', t: 'Choose age group' },
              { s: '3', t: 'Enter salary income' },
              { s: '4', t: 'Add other income' },
              { s: '5', t: 'Enter deductions' },
              { s: '6', t: 'Click Calculate' }
            ].map((item, i) => (
              <div key={i} style={{ backgroundColor: '#f9fafb', padding: '20px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid #e5e7eb' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#115e59', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'Onest, sans-serif', color: '#fff', fontWeight: 600, fontSize: '14px' }}>{item.s}</span>
                </div>
                <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#374151', fontWeight: 500 }}>{item.t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '20px' }}>Why Choose Kapitalised?</h3>
          <ul style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#374151', lineHeight: 1.8, paddingLeft: '0', listStyle: 'none' }}>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}><span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#115e59" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></span>Accurate tax calculation</li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}><span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#115e59" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></span>Updated as per latest budget</li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}><span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#115e59" strokeWidth="2"><path d="M3 3v18h18"/><path d="M7 16l4-8 4 5 5-6"/></svg></span>Easy comparison of tax regimes</li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}><span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#115e59" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg></span>Simple interface</li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#115e59" strokeWidth="2"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg></span>Better tax planning</li>
          </ul>
        </div>

      </div>
      {/* End SEO Section */}

    </div>
  );
};

export default TaxCalculator;
